import React, { useEffect, useState } from 'react';
import { api } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Target, Trophy, Sparkles } from 'lucide-react';

export default function MyObjectiveWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/dashboard/my-objective')
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) return null;

  const pctRdv = data.pourcentage_rdv || 0;
  const pctCv = data.pourcentage_convertis || 0;

  const pillRdv = pctRdv >= 100 ? 'bg-[#10B981]' : pctRdv >= 60 ? 'bg-[#F97316]' : 'bg-[#0055FF]';
  const pillCv = pctCv >= 100 ? 'bg-[#10B981]' : pctCv >= 60 ? 'bg-[#8B5CF6]' : 'bg-[#0055FF]';
  const isStar = pctRdv >= 100;

  return (
    <Card className="border-2 border-[#F97316]/15 shadow-sm bg-gradient-to-br from-white via-[#FFF7ED]/40 to-white" data-testid="my-objective-widget">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#F97316] to-[#EA580C] flex items-center justify-center text-white shadow-sm">
              {isStar ? <Trophy className="w-4 h-4" /> : <Target className="w-4 h-4" />}
            </div>
            <div>
              <h3 className="text-sm font-semibold tracking-tight">Mon objectif — {data.mois_label}</h3>
              <p className="text-[11px] text-[#71717A] mt-0.5">
                {data.secteur_size} prospects sur mon secteur · {data.rdv_actifs_secteur} RDV actifs
              </p>
            </div>
          </div>
          {isStar && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#10B981]/10 text-[#059669] text-[11px] font-semibold" data-testid="my-objective-star">
              <Sparkles className="w-3 h-3" /> Objectif atteint
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* RDV décrochés */}
          <div className="rounded-xl border border-[#E5E7EB] bg-white p-4" data-testid="objective-rdv-block">
            <div className="flex items-baseline justify-between mb-2">
              <span className="text-xs uppercase tracking-wider text-[#71717A] font-medium">RDV décrochés</span>
              <span className="text-[11px] text-[#71717A] tabular-nums">{pctRdv}%</span>
            </div>
            <div className="flex items-baseline gap-1.5 mb-2.5">
              <span className="text-3xl font-bold tabular-nums" style={{ fontFamily: 'var(--font-heading)' }} data-testid="objective-rdv-value">
                {data.rdv_obtenus}
              </span>
              <span className="text-sm text-[#71717A]">/ {data.objectif_rdv}</span>
            </div>
            <div className="h-2 bg-[#F3F4F6] rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all duration-500 ${pillRdv}`} style={{ width: `${pctRdv}%` }} />
            </div>
          </div>

          {/* Convertis */}
          <div className="rounded-xl border border-[#E5E7EB] bg-white p-4" data-testid="objective-convertis-block">
            <div className="flex items-baseline justify-between mb-2">
              <span className="text-xs uppercase tracking-wider text-[#71717A] font-medium">Convertis</span>
              <span className="text-[11px] text-[#71717A] tabular-nums">{pctCv}%</span>
            </div>
            <div className="flex items-baseline gap-1.5 mb-2.5">
              <span className="text-3xl font-bold tabular-nums" style={{ fontFamily: 'var(--font-heading)' }} data-testid="objective-convertis-value">
                {data.convertis}
              </span>
              <span className="text-sm text-[#71717A]">/ {data.objectif_convertis}</span>
            </div>
            <div className="h-2 bg-[#F3F4F6] rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all duration-500 ${pillCv}`} style={{ width: `${pctCv}%` }} />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
